import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, setSession, FRANJAS } from '../api.js';
import { Logo } from '../components/Logo.jsx';

export default function Login() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState(localStorage.getItem('lc_last_tenant') ?? '1');
  const [nombre, setNombre] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sugerencia, setSugerencia] = useState(null); // { franja, fecha, usuario_nombre }
  const [usuarioEdited, setUsuarioEdited] = useState(false);

  useEffect(() => {
    const tid = Number(tenantId);
    if (!tid) return;
    let cancel = false;
    api.horarioActualPublico(tid)
      .then((r) => {
        if (cancel) return;
        setSugerencia(r);
        if (r?.usuario_nombre && !usuarioEdited) setNombre(r.usuario_nombre);
      })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [tenantId]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await api.login({ tenantId: Number(tenantId), nombre, pin });
      setSession(data);
      localStorage.setItem('lc_last_tenant', tenantId);
      navigate('/');
    } catch (err) {
      setError('PIN o nombre incorrecto.');
    } finally {
      setLoading(false);
    }
  };

  const franjaInfo = sugerencia ? FRANJAS.find((f) => f.codigo === sugerencia.franja) : null;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Lado izquierdo · hero */}
      <div className="lg:w-1/2 lg:min-h-screen text-paper p-8 lg:p-16 flex flex-col justify-between relative overflow-hidden grain" style={{ background: 'linear-gradient(135deg, #1B2A4E 0%, #2C3E66 60%, #1B2A4E 100%)' }}>
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(closest-side, #C8E847, transparent)' }} />
        <div className="absolute -left-32 -bottom-32 w-[400px] h-[400px] rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(closest-side, #C8E847, transparent)' }} />

        <div className="relative">
          <Logo size={56} withWordmark dark />
        </div>

        <div className="relative mt-12 lg:mt-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-lime mb-4">Contabilidad asistida</p>
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Cuentas claras,<br />sueño tranquilo.
          </h1>
          <p className="text-base lg:text-lg text-paper/80 max-w-md leading-relaxed">
            Una sola pantalla para llevar la contabilidad de todas las empresas que asesoras. Turnos, ventas, cortesías y cuadres, sin papeles ni hojas perdidas.
          </p>
        </div>

        <div className="relative pt-12 lg:pt-0 flex items-center gap-4 text-paper/60 font-mono text-[10px] uppercase tracking-[0.18em]">
          <div>SLC S.A.S</div>
          <span className="opacity-30">·</span>
          <div>NIT 901.587.923-0</div>
          <span className="opacity-30">·</span>
          <div>Tuluá</div>
        </div>
      </div>

      {/* Lado derecho · formulario */}
      <div className="lg:w-1/2 lg:min-h-screen flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <p className="eyebrow mb-2">Acceso</p>
          <h2 className="text-3xl font-bold tracking-tight mb-2">Entrar al turno.</h2>
          <p className="text-muted mb-10">Identifícate con tu nombre y PIN para abrir la jornada.</p>

          {sugerencia && franjaInfo && (
            <div className="mb-6 rounded-2xl px-4 py-3 border" style={{ background: 'rgba(200,232,71,0.18)', borderColor: 'rgba(168,201,50,0.35)' }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">Turno actual</div>
              <div className="font-semibold tracking-tight mt-1">
                {franjaInfo.short} · <span className="font-mono text-sm">{franjaInfo.label}</span>
              </div>
              <div className="text-sm mt-1 text-ink/80">
                {sugerencia.usuario_nombre
                  ? <>Le toca a <b>{sugerencia.usuario_nombre}</b>.</>
                  : <span className="text-muted">Sin asignación. Avísale al admin para que cargue el horario.</span>}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label">Empresa</label>
              <input className="input" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="1" />
            </div>
            <div>
              <label className="label">Tu nombre</label>
              <input className="input" value={nombre} onChange={(e) => { setNombre(e.target.value); setUsuarioEdited(true); }} autoComplete="username" required />
              {sugerencia?.usuario_nombre && nombre && nombre.trim().toLowerCase() !== sugerencia.usuario_nombre.trim().toLowerCase() && (
                <div className="font-mono text-[10px] mt-1 text-orange-600">⚠ No es tu turno según el horario ({sugerencia.usuario_nombre}).</div>
              )}
            </div>
            <div>
              <label className="label">PIN</label>
              <input className="input" type="password" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="current-password" inputMode="numeric" required />
            </div>
            {error && (
              <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 font-mono">
                {error}
              </div>
            )}
            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading ? 'Entrando…' : 'Entrar →'}
            </button>
          </form>

          <Link to="/setup" className="block text-center mt-10 font-mono text-[11px] uppercase tracking-[0.18em] text-muted hover:text-ink transition">
            ¿Primera vez? Inicializar empresa →
          </Link>
        </div>
      </div>
    </div>
  );
}
